import type { ReactNode } from "react";
import { CR_ROW_LABEL, CR_ROW_LABEL_WITH_MARKERS } from "../../../theme/catalogThemeClasses";

/**
 * @param e2b the field is reported to the regulator in an E2B submission, so it is marked as such
 *   wherever it is read. It is what the field *is*, not whether it is enforced today — the setting
 *   that gates E2B completeness at routing is a different thing and does not hide the mark (D56).
 * @param e2bRequired the field is mandatory for E2B, has no null-flavor escape, and is still empty —
 *   a readiness condition the reviewer needs to see, not a blocker (the server reports it at routing).
 */
export function CaseFieldLabel({ inputId, label, required, dirty, e2b = false, e2bRequired = false }:
  { inputId: string; label: string; required: boolean; dirty: boolean; e2b?: boolean; e2bRequired?: boolean }) {
  const hasMarkers = dirty || e2b || e2bRequired;
  return <label className={`${CR_ROW_LABEL}${hasMarkers ? ` ${CR_ROW_LABEL_WITH_MARKERS}` : ""}`} htmlFor={inputId} title={required ? `${label} · Required` : label}>
    <span className="cr-row-label-text">{label}
      {required && <><span className="cr-required" aria-hidden="true">{"\u00a0*"}</span><span className="sr-only"> (required)</span></>}
    </span>
    {hasMarkers && <span className="cr-row-label-markers">
      {e2bRequired && <span className="cr-e2b-required" title="Required for the E2B submission" aria-label="Required for the E2B submission">!</span>}
      {e2b && <span className="cr-e2b-badge" title="Reported in the E2B submission">E2B</span>}
      {dirty && <span className="cr-dot" title="Unsaved change" />}
    </span>}
  </label>;
}

export function CaseFieldErrors({ inputId, errors }: { inputId: string; errors: string }) {
  return errors ? <p id={`${inputId}-errors`} className="cr-error">{errors}</p> : null;
}

interface Props {
  label: ReactNode; children: ReactNode; confidence: ReactNode; actions: ReactNode;
  selected: boolean; pendingAi: boolean; hasError: boolean; onFocus: () => void;
  /**
   * A second value cell between the value and the confidence, for a table that compares two values
   * of one field (a follow-up's current vs incoming). The table it sits in widens `--cr-grid` by one
   * column; an ordinary section never passes it.
   */
  incoming?: ReactNode;
  /** The state of Theragenie's proposal on this field, painted as `data-proposal`; absent without one. */
  proposalState?: string;
  /**
   * Long free text (a narrative): the value takes a line of its own under the label, across the
   * whole row, so a paragraph can be read and edited without scrolling a narrow box.
   */
  wide?: boolean;
}

/** One field row of the table: label · value · [incoming ·] AI confidence · action. */
export function CaseFieldReviewTableRow({ label, children, confidence, actions, selected, pendingAi, hasError, onFocus, incoming, proposalState, wide = false }: Props) {
  // `cr-field` is the focus fallback the review hook looks for when a failed control is disabled.
  return <div className="cr-row cr-field" role="row" tabIndex={-1} data-selected={selected} data-ai={pendingAi} data-error={hasError} data-proposal={proposalState} data-wide={wide && incoming === undefined ? true : undefined} onFocus={onFocus} onClick={onFocus}>
    {label}
    <div className="cr-row-value">{children}</div>
    {incoming !== undefined && <div className="cr-row-incoming">{incoming}</div>}
    <div className="cr-row-conf">{confidence}</div>
    <div className="cr-row-actions">{actions}</div>
  </div>;
}
